import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'deployment-methods', title: '1. Deployment Methods' },
  { id: 'script-embed', title: '2. Script Tag Embed' },
  { id: 'iframe-embed', title: '3. Iframe Embed' },
  { id: 'direct-link', title: '4. Direct Link & QR Code' },
  { id: 'customization', title: '5. Customization Options' },
  { id: 'deployment-security', title: '6. Deployment Security' },
  { id: 'troubleshooting', title: '7. Troubleshooting' },
];

const WebsiteIntegrationPage = () => {
  return (
    <>
      <NextSeo
        title="Website Integration Guide"
        description="Learn how to embed your Chat.co chatbot on any website using script tags, iframes, or direct links with full customization options."
        canonical="https://chat.co/docs/website-integration"
        openGraph={{
          url: 'https://chat.co/docs/website-integration',
          title: 'Website Integration Guide | Chat.co',
          description:
            'Learn how to embed your Chat.co chatbot on any website using script tags, iframes, or direct links with full customization options.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-01-26T00:00:00Z',
            section: 'Integrations',
            tags: ['website embed', 'chatbot integration', 'iframe', 'script tag'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - Website Integration',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Integrations"
        categoryColor="text-orange-700"
        title="Website Integration"
        description="Embed your AI chatbot on any website with a single line of code."
        readingTime="8 min read"
        lastUpdated="January 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'API Documentation',
          href: PageLink.docsApiReference,
        }}
        nextArticle={{
          title: 'Debugging Guide',
          href: PageLink.docsTroubleshooting,
        }}
        keyTakeaways={[
          'Script tag embed is the easiest—just one line of code',
          'Iframe embed gives you more layout control',
          'Direct links and QR codes work for offline/print materials',
          'Customize colors, position, and welcome messages',
          'Use domain restrictions to control where your bot appears',
        ]}>
        {/* Introduction */}
        <p className="lead text-lg text-gray-700">
          Once you&apos;ve built and tested your chatbot, it&apos;s time to deploy it. Chat.co
          offers multiple ways to add your chatbot to any website—from a simple script tag to fully
          customized iframe embeds. This guide covers all deployment options and customization
          settings.
        </p>

        {/* Section 1: Deployment Methods */}
        <section id="deployment-methods" className="scroll-mt-24">
          <h2>1. Deployment Methods</h2>

          <p>Chat.co provides three ways to deploy your chatbot. Choose based on your needs:</p>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Icon icon="tabler:code" className="text-lg text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Script Tag (Recommended)</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    A floating chat bubble that appears on every page. Best for most websites.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">
                      Easiest setup
                    </span>
                    <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                      Non-intrusive
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100">
                  <Icon icon="tabler:layout" className="text-lg text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Iframe Embed</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Embed the chat directly into a page section. Best for help centers and dedicated
                    support pages.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-700">
                      Inline placement
                    </span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                      Custom sizing
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                  <Icon icon="tabler:link" className="text-lg text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Direct Link</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    A standalone page URL you can share anywhere. Best for email, social media, or
                    QR codes.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">
                      No code required
                    </span>
                    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                      QR code support
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h3>Finding Your Embed Code</h3>
          <ol>
            <li>Go to your chatbot&apos;s dashboard</li>
            <li>
              Click <code>Deployment</code> in the sidebar
            </li>
            <li>Choose your deployment method</li>
            <li>Copy the code or link</li>
          </ol>
        </section>

        {/* Section 2: Script Tag Embed */}
        <section id="script-embed" className="scroll-mt-24">
          <h2>2. Script Tag Embed</h2>

          <p>
            The script tag is the easiest way to add a chat widget to your website. It creates a
            floating bubble that expands into a full chat window when clicked.
          </p>

          <h3>Basic Installation</h3>
          <p>
            Add this code just before the closing <code>&lt;/body&gt;</code> tag on any page:
          </p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`<script
  defer
  src="https://chat.co/api/embed"
  data-bot-id="YOUR_BOT_ID"
></script>`}
            </pre>
          </div>

          <p>
            Replace <code>YOUR_BOT_ID</code> with your actual chatbot ID (found on the Deployment
            page).
          </p>

          <h3>How It Works</h3>
          <ul>
            <li>
              <strong>Floating bubble</strong> — A circular button appears in the corner of the page
            </li>
            <li>
              <strong>Hover preview</strong> — Shows &quot;Ask AI&quot; text when users hover
            </li>
            <li>
              <strong>Click to open</strong> — Expands into a full chat window
            </li>
            <li>
              <strong>Mobile responsive</strong> — Goes full-screen on mobile devices
            </li>
          </ul>

          <h3>Platform-Specific Instructions</h3>
          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:brand-wordpress" className="text-blue-500" />
                WordPress
              </h4>
              <ol className="mt-2 text-sm text-gray-600">
                <li>Go to Appearance → Theme Editor</li>
                <li>
                  Select <code>footer.php</code>
                </li>
                <li>
                  Paste the script before <code>&lt;/body&gt;</code>
                </li>
                <li>Save changes</li>
              </ol>
              <p className="mt-2 text-xs text-gray-500">
                Or use a plugin like &quot;Insert Headers and Footers&quot; for easier management.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:brand-shopify" className="text-green-500" />
                Shopify
              </h4>
              <ol className="mt-2 text-sm text-gray-600">
                <li>Go to Online Store → Themes</li>
                <li>Click Actions → Edit code</li>
                <li>
                  Open <code>theme.liquid</code>
                </li>
                <li>
                  Paste the script before <code>&lt;/body&gt;</code>
                </li>
                <li>Save</li>
              </ol>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:brand-wix" className="text-black" />
                Wix
              </h4>
              <ol className="mt-2 text-sm text-gray-600">
                <li>Go to Settings → Custom Code</li>
                <li>Click &quot;Add Custom Code&quot;</li>
                <li>Paste the script</li>
                <li>Set placement to &quot;Body - end&quot;</li>
                <li>Apply to &quot;All pages&quot;</li>
              </ol>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon icon="tabler:brand-react" className="text-cyan-500" />
                React / Next.js
              </h4>
              <div className="mt-2 overflow-x-auto rounded bg-gray-900 p-3">
                <pre className="text-xs text-gray-300">
                  {`// In your _app.tsx or layout component
import Script from 'next/script';

export default function App({ Component, pageProps }) {
  return (
    <>
      <Component {...pageProps} />
      <Script
        src="https://chat.co/api/embed"
        data-bot-id="YOUR_BOT_ID"
        strategy="lazyOnload"
      />
    </>
  );
}`}
                </pre>
              </div>
            </div>
          </div>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> The <code>defer</code> attribute ensures the script loads after
              your page content, preventing any performance impact on page load times.
            </p>
          </div>
        </section>

        {/* Section 3: Iframe Embed */}
        <section id="iframe-embed" className="scroll-mt-24">
          <h2>3. Iframe Embed</h2>

          <p>
            Use an iframe when you want to embed the chat directly into a page section rather than
            as a floating widget.
          </p>

          <h3>Basic Iframe Code</h3>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`<iframe
  src="https://chat.co/embed/YOUR_BOT_ID"
  style="width: 400px; height: 600px; border: none;"
  title="Chat Assistant"
></iframe>`}
            </pre>
          </div>

          <h3>Responsive Iframe</h3>
          <p>Make the iframe adapt to its container:</p>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`<div style="width: 100%; max-width: 500px; height: 700px;">
  <iframe
    src="https://chat.co/embed/YOUR_BOT_ID"
    style="width: 100%; height: 100%; border: none; border-radius: 12px;"
    title="Chat Assistant"
  ></iframe>
</div>`}
            </pre>
          </div>

          <h3>When to Use Iframe</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <h4 className="flex items-center gap-2 font-medium text-green-900">
                <Icon icon="tabler:check" className="text-green-500" />
                Good for
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-green-800">
                <li>Dedicated help center pages</li>
                <li>Contact pages</li>
                <li>Knowledge base sections</li>
                <li>Support portals</li>
              </ul>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <h4 className="flex items-center gap-2 font-medium text-red-900">
                <Icon icon="tabler:x" className="text-red-500" />
                Not ideal for
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-red-800">
                <li>Site-wide availability</li>
                <li>E-commerce product pages</li>
                <li>Mobile-first sites</li>
                <li>Pages with limited space</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 4: Direct Link */}
        <section id="direct-link" className="scroll-mt-24">
          <h2>4. Direct Link & QR Code</h2>

          <p>
            Sometimes you don&apos;t need to embed anything—just share a link to your chatbot&apos;s
            standalone page.
          </p>

          <h3>Your Chatbot Link</h3>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-100 p-4">
            <code className="text-sm text-gray-800">https://chat.co/embed/YOUR_BOT_ID</code>
          </div>

          <h3>Use Cases</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:mail" className="mt-0.5 text-lg text-gray-500" />
              <div>
                <h4 className="font-medium text-gray-900">Email signatures</h4>
                <p className="text-sm text-gray-600">
                  Add &quot;Chat with our AI assistant&quot; link to employee email signatures.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:brand-whatsapp" className="mt-0.5 text-lg text-green-500" />
              <div>
                <h4 className="font-medium text-gray-900">Messaging apps</h4>
                <p className="text-sm text-gray-600">
                  Share via WhatsApp, Slack, or Teams for quick customer access.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:qrcode" className="mt-0.5 text-lg text-gray-500" />
              <div>
                <h4 className="font-medium text-gray-900">Print materials</h4>
                <p className="text-sm text-gray-600">
                  Generate a QR code for brochures, business cards, or trade show booths.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:brand-linkedin" className="mt-0.5 text-lg text-blue-600" />
              <div>
                <h4 className="font-medium text-gray-900">Social media bios</h4>
                <p className="text-sm text-gray-600">
                  Add to your company&apos;s social profiles for instant support access.
                </p>
              </div>
            </div>
          </div>

          <h3>QR Code Generation</h3>
          <p>
            Chat.co automatically generates a QR code for your chatbot link. Find it on the
            Deployment page and download it in SVG format for print-ready quality.
          </p>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> QR codes work great at trade shows and events. Print them on
              table signs or badges so visitors can instantly access your AI assistant.
            </p>
          </div>
        </section>

        {/* Section 5: Customization */}
        <section id="customization" className="scroll-mt-24">
          <h2>5. Customization Options</h2>

          <p>
            Make your chatbot match your brand with extensive appearance customization. All settings
            are configured in your chatbot&apos;s Appearance tab.
          </p>

          <h3>Color Customization</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Setting
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    What It Changes
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Default
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Window Background
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Main chat area background
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-4 w-4 rounded border"
                        style={{ backgroundColor: '#F0F8FF' }}
                      />
                      <code className="text-xs">#F0F8FF</code>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Header Color
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Top header bar
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-4 w-4 rounded border"
                        style={{ backgroundColor: '#004790' }}
                      />
                      <code className="text-xs">#004790</code>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    User Message
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    User&apos;s chat bubble
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-4 w-4 rounded border"
                        style={{ backgroundColor: '#004790' }}
                      />
                      <code className="text-xs">#004790</code>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Bot Message
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    AI&apos;s chat bubble
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-4 w-4 rounded border"
                        style={{ backgroundColor: '#FFFFFF' }}
                      />
                      <code className="text-xs">#FFFFFF</code>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Send Button
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Send message button
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block h-4 w-4 rounded border"
                        style={{ backgroundColor: '#004790' }}
                      />
                      <code className="text-xs">#004790</code>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Widget Position</h3>
          <p>Choose where the chat bubble appears:</p>
          <ul>
            <li>
              <strong>Right</strong> (default) — Bottom-right corner
            </li>
            <li>
              <strong>Left</strong> — Bottom-left corner
            </li>
          </ul>

          <h3>Branding Options</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:photo" className="mt-0.5 text-lg text-gray-500" />
              <div>
                <h4 className="font-medium text-gray-900">Custom Logo</h4>
                <p className="text-sm text-gray-600">
                  Upload your company logo or avatar to display in the chat header and bubble.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:badge" className="mt-0.5 text-lg text-gray-500" />
              <div>
                <h4 className="font-medium text-gray-900">Hide Chat.co Branding</h4>
                <p className="text-sm text-gray-600">
                  Remove &quot;Powered by Chat.co&quot; for a fully white-labeled experience (Pro
                  plan and above).
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:pencil" className="mt-0.5 text-lg text-gray-500" />
              <div>
                <h4 className="font-medium text-gray-900">Custom Branding Text</h4>
                <p className="text-sm text-gray-600">
                  Replace the default branding with your own text (e.g., &quot;Powered by Your
                  Company&quot;).
                </p>
              </div>
            </div>
          </div>

          <h3>Content Customization</h3>
          <ul>
            <li>
              <strong>Chat Title</strong> — The name shown in the header
            </li>
            <li>
              <strong>Welcome Message</strong> — First message users see when opening the chat
            </li>
            <li>
              <strong>Placeholder Text</strong> — Text shown in the empty input field
            </li>
            <li>
              <strong>Suggested Questions</strong> — Quick-reply buttons for common questions
            </li>
          </ul>

          <h3>Advanced Features</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:forms" className="text-xl text-primary-500" />
              <h4 className="mt-2 font-semibold text-gray-900">Lead Form</h4>
              <p className="mt-1 text-sm text-gray-600">
                Collect name, email, phone before or during chat. Configurable required fields.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:microphone" className="text-xl text-primary-500" />
              <h4 className="mt-2 font-semibold text-gray-900">Voice Input/Output</h4>
              <p className="mt-1 text-sm text-gray-600">
                Enable voice chat with multiple voice options via AWS Polly.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:quote" className="text-xl text-primary-500" />
              <h4 className="mt-2 font-semibold text-gray-900">Citations</h4>
              <p className="mt-1 text-sm text-gray-600">
                Show or hide source citations in AI responses.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:upload" className="text-xl text-primary-500" />
              <h4 className="mt-2 font-semibold text-gray-900">Document Upload</h4>
              <p className="mt-1 text-sm text-gray-600">
                Allow users to upload documents during chat for analysis.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: Deployment Security */}
        <section id="deployment-security" className="scroll-mt-24">
          <h2>6. Deployment Security</h2>

          <p>
            Protect your chatbot with security settings to control who can access it and from where.
          </p>

          <h3>Security Options</h3>
          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                  <Icon icon="tabler:lock" className="text-lg text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Password Protection</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Require a password to access your chatbot. Passwords are securely hashed with
                    bcrypt. Includes rate limiting (5 attempts per minute) to prevent brute force
                    attacks.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Icon icon="tabler:clock" className="text-lg text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Link Expiration</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Set your deployment link to expire after a specific period:
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      1 day
                    </span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      7 days
                    </span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      30 days
                    </span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      90 days
                    </span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      Never
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                  <Icon icon="tabler:list-check" className="text-lg text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">IP Whitelist</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Only allow access from specific IP addresses. Perfect for internal tools or
                    office-only access.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100">
                  <Icon icon="tabler:ban" className="text-lg text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">IP Blacklist</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Block access from specific IP addresses. Useful for stopping abuse or unwanted
                    traffic.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h3>Enabling Security</h3>
          <ol>
            <li>Go to your chatbot&apos;s Deployment page</li>
            <li>Toggle &quot;Enable Deployment Security&quot;</li>
            <li>Configure your desired security options</li>
            <li>Save changes</li>
          </ol>

          <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              <strong>Note:</strong> Security settings apply to all deployment methods (script,
              iframe, and direct link). When password protection is enabled, users must enter the
              password before they can use the chatbot.
            </p>
          </div>
        </section>

        {/* Section 7: Troubleshooting */}
        <section id="troubleshooting" className="scroll-mt-24">
          <h2>7. Troubleshooting</h2>

          <p>Common issues and how to fix them.</p>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Widget doesn&apos;t appear</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>
                  Check that the script is placed before <code>&lt;/body&gt;</code>
                </li>
                <li>Verify the bot ID is correct</li>
                <li>Make sure your chatbot is set to &quot;Active&quot;</li>
                <li>Check browser console for JavaScript errors</li>
                <li>Disable ad blockers (some block chat widgets)</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">
                Widget appears but chat doesn&apos;t load
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Check if deployment security is blocking access</li>
                <li>Verify the chatbot has active sources/training data</li>
                <li>Ensure you haven&apos;t exceeded message limits</li>
                <li>Try clearing browser cache and cookies</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Iframe shows blank or error</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Check if your site uses HTTPS (required for embedding)</li>
                <li>Verify the iframe src URL is correct</li>
                <li>Some sites block iframes—check Content Security Policy headers</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Styling conflicts</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>The widget uses isolated styles, but some global CSS can interfere</li>
                <li>
                  Check for CSS rules targeting <code>iframe</code> or <code>div</code> broadly
                </li>
                <li>Use browser dev tools to inspect conflicting styles</li>
              </ul>
            </div>
          </div>

          <div className="my-8 rounded-xl border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                <Icon icon="tabler:help" className="text-xl text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary-900">Still having issues?</h4>
                <p className="mt-1 text-primary-800">
                  Contact our support team at{' '}
                  <a href="mailto:support@chat.co" className="underline">
                    support@chat.co
                  </a>{' '}
                  with your chatbot ID and a description of the problem. We&apos;re happy to help!
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <h3>Next Steps</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <Link
              href={PageLink.docsTroubleshooting}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:bug"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Debugging Guide</h4>
              <p className="mt-1 text-sm text-gray-600">Diagnose and fix common chatbot issues.</p>
            </Link>
            <Link
              href={PageLink.docsSecurityCompliance}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:shield-check"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Security & Compliance</h4>
              <p className="mt-1 text-sm text-gray-600">
                Learn about enterprise security best practices.
              </p>
            </Link>
          </div>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default WebsiteIntegrationPage;
